import React from "react";
import PropTypes from "prop-types";
import {Box, CircularProgress} from "@mui/material";
import Cookies from "js-cookie";
import {BreachRxAuthProvider} from "@breachrx/client-auth";
import {useAuth} from "react-oidc-context";
import {LoginContext} from "./LoginProvider";
import {buildOidcConfig, deriveRegScoutPlan, useKeycloakUser} from "./keycloakHelpers";

const oidcConfig = buildOidcConfig();

// Guard so concurrent 401s trigger only one redirect.
let reauthInFlight = false;

const KeycloakLoader = () => (
  <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
    <CircularProgress />
  </Box>
);

const LoginContextAdapter = ({children}) => {
  const auth = useAuth();
  const {user, groups} = useKeycloakUser();
  const {isAdmin, isHighTier, isPaid, isProfessional, needsUpgrade, currentPlanForDisplay} =
    deriveRegScoutPlan(groups);

  const isDarkThemeSaved = Cookies.get("theme") === "dark";

  const forceReauth = async () => {
    if (reauthInFlight) {
      return;
    }
    reauthInFlight = true;
    try {
      await auth.removeUser();
      await auth.signinRedirect();
    } finally {
      // Reset so a blocked/failed redirect (popup blocked, test env) doesn't wedge re-auth.
      reauthInFlight = false;
    }
  };

  // Never replay the rejected token — it would loop on repeated 401s. Silent-renew
  // if possible, otherwise force an interactive re-login.
  const refreshSession = async () => {
    try {
      const refreshed = await auth.signinSilent();
      if (refreshed?.id_token) {
        return {IdToken: refreshed.id_token};
      }
    } catch {
      // fall through to re-auth
    }
    await forceReauth();
    return {};
  };

  const logout = async () => {
    try {
      await auth.signoutRedirect();
    } catch {
      await auth.removeUser();
      window.location.href = window.location.origin;
    }
  };

  return (
    <LoginContext value={{
      login: (args) => auth.signinRedirect(args),
      loginSchema: {},
      user,
      setUser: () => {},
      currentPlanForDisplay,
      groups,
      isAdmin,
      isHighTier,
      isPaid,
      isProfessional,
      needsUpgrade,
      refreshSession,
      refreshError: {},
      setRefreshError: () => {},
      logout,
      isDarkThemeSaved,
      forgotPassword: () => {
        window.location.href = `${process.env.AUTH_AUTHORITY}/account/#/security/signingin`;
      },
      resetPassword: () => {},
      isSubmitting: false,
      mfaChallenge: {},
      setMFAChallenge: () => {},
      mfaSchema: {},
      submitMfa: () => {},
      updateCustomerProfile: () => {},
    }}
    >
      {children}
    </LoginContext>
  );
};

LoginContextAdapter.propTypes = {
  children: PropTypes.node.isRequired,
};

const KeycloakLoginProvider = ({children}) => (
  <BreachRxAuthProvider config={oidcConfig} LoaderComponent={<KeycloakLoader />}>
    <LoginContextAdapter>{children}</LoginContextAdapter>
  </BreachRxAuthProvider>
);

KeycloakLoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default KeycloakLoginProvider;
